import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  ChatBubbleOvalLeftEllipsisIcon,
  ArrowRightIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

interface FloatingInputProps {
  onSend: (value: string) => void;
  disabled?: boolean;
  loading?: boolean;
  autoCollapseDelay?: number;
}

export const FloatingInput: React.FC<FloatingInputProps> = ({
  onSend,
  disabled = false,
  loading = false,
  autoCollapseDelay = 5000,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const idleTimer = useRef<NodeJS.Timeout | null>(null);

  // Expand
  const handleExpand = () => {
    if (disabled) return;
    setExpanded(true);
    setTimeout(() => inputRef.current?.focus(), 250);
  };

  // Collapse
  const handleCollapse = () => {
    setExpanded(false);
    setInputValue("");
  };

  // Send
  const handleSend = () => {
    if (!inputValue.trim() || disabled) return;
    onSend(inputValue.trim());
    setInputValue("");
    setExpanded(false);
  };

  // Auto collapse when idle
  const resetIdleTimer = () => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => {
      if (!inputValue.trim() && document.activeElement !== inputRef.current) {
        handleCollapse();
      }
    }, autoCollapseDelay);
  };

  useEffect(() => {
    if (!expanded) return;
  
    // Helper: reset or start idle timer
    const resetIdleTimer = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => {
        // Collapse only if input is empty and not focused
        const inputEl = inputRef.current;
        const isFocused = inputEl && document.activeElement === inputEl;
        if (!isFocused && !inputValue.trim()) {
          handleCollapse();
        }
      }, autoCollapseDelay);
    };
  
    // Start timer when expanded
    resetIdleTimer();
  
    // Collapse immediately if blurred & empty
    const handleBlur = () => {
      if (!inputValue.trim()) {
        // Give a tiny delay so it doesn't collapse during click on buttons
        setTimeout(() => handleCollapse(), 300);
      }
    };
  
    // Reset timer when user types or interacts
    const handleActivity = () => resetIdleTimer();
  
    // Add listeners
    const inputEl = inputRef.current;
    inputEl?.addEventListener("blur", handleBlur);
    window.addEventListener("scroll", handleActivity);
    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keydown", handleActivity);
    window.addEventListener("click", handleActivity);
  
    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      inputEl?.removeEventListener("blur", handleBlur);
      window.removeEventListener("scroll", handleActivity);
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("click", handleActivity);
    };
  }, [expanded, inputValue, autoCollapseDelay]);

  const isDisabled = disabled || loading;

  return (
    <motion.div
      layoutId="fabToBar"
      className="fixed bottom-6 right-6 z-[999] flex items-center justify-end"
      initial={false}
      animate={{
        width: expanded ? "min(80vw)" : 72, // ✅ bigger FAB + wider bar
        height: 72, // ✅ bigger FAB
        borderRadius: expanded ? 20 : 9999,
        backgroundColor: expanded
          ? "rgba(249,250,251,1)"
          : "rgba(55,42,172,1)",
        boxShadow: "0px 4px 14px rgba(0,0,0,0.18)",
      }}
      transition={{
        type: "spring",
        stiffness: 130,
        damping: 18,
      }}
      style={{
        overflow: "hidden",
      }}
    >
      {!expanded ? (
        // 🟣 FAB Mode
        <button
          onClick={handleExpand}
          disabled={isDisabled}
          className="w-full h-full flex items-center justify-center text-white focus:outline-none"
        >
          {loading ? (
            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <ChatBubbleOvalLeftEllipsisIcon className="w-7 h-7" /> // ✅ slightly larger icon
          )}
        </button>
      ) : (
        // 🟢 Expanded Input Mode
        <div className="flex items-center justify-between w-full px-5 py-2">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              resetIdleTimer();
            }}
            onFocus={resetIdleTimer}
            onBlur={resetIdleTimer}
            disabled={isDisabled}
            placeholder="Ask IS"
            className="flex-1 bg-transparent outline-none text-[15px] text-gray-700 placeholder-gray-400 pr-3"
          />
          <button
            onClick={handleCollapse}
            disabled={isDisabled}
            className="text-gray-400 hover:text-gray-600 transition mr-1"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
          <button
            onClick={handleSend}
            disabled={loading || isDisabled}
            className="bg-[rgba(55,42,172,1)] text-white rounded-full p-3 hover:bg-[rgba(49,44,133,1)] transition disabled:opacity-50"
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <ArrowRightIcon className="w-6 h-6" />
            )}
          </button>
        </div>
      )}
    </motion.div>
  );
};
